<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb
          parentRouteName="BookingPresetList"
          :extra-items="[
            {
              text: bookingPreset.name,
              to: 'BookingCenterBookingList',
              params: $route.params.id
            },
            '報表設定'
          ]"
        />
      </div>
    </div>

    <div class="email-wrapper wrapper">
      <div class="row bg-white">
        <div class="col-md-2 mail-sidebar pt-3">
          <div class="menu-bar">
            <ul class="menu-items">
              <li
                :class="{ active: selectedReportIndex === null }"
                @click="createNewReport"
              >
                <a>
                  <i class="mdi mdi-plus"></i>
                  新增報表
                </a>
              </li>
              <li
                v-for="(report, index) in reports"
                :key="'report-' + index"
                :class="{ active: selectedReportIndex === index }"
                @click="selectReport(index)"
              >
                <a>{{ report.title || `報表 ${index + 1}` }}</a>
              </li>
            </ul>
          </div>
        </div>

        <div class="col-md-10 main-view">
          <b-overlay :show="showLoading" rounded="sm">
            <b-card>
              <h4 class="mb-4 font-weight-bold">自訂報表</h4>

              <b-form-group
                label-cols="12"
                label-cols-lg="2"
                label-size="sm"
                label="報表名稱"
              >
                <b-form-input
                  v-model="currentForm.title"
                  placeholder="請輸入報表名稱"
                />
              </b-form-group>

              <div class="report-fields">
                <VueDraggable
                  handle=".option-drag-handler"
                  :list="currentForm.fields"
                  class="report-fields__list"
                >
                  <div
                    v-for="(field, fieldIndex) in currentForm.fields"
                    :key="'field-' + fieldIndex"
                    class="report-fields__row"
                    :class="{ 'report-fields__row--last': fieldIndex === currentForm.fields.length - 1 }"
                  >
                    <span class="report-fields__column-label">
                      第 {{ fieldIndex + 1 }} 欄
                    </span>
                    <i class="fa fa-bars option-drag-handler"></i>
                    <b-form-select
                      v-model="currentForm.fields[fieldIndex]"
                      :options="fieldOptions"
                      class="report-fields__select"
                    />
                    <b-button
                      v-if="fieldIndex === currentForm.fields.length - 1"
                      class="p-1 d-inline-flex align-items-center ml-2 flex-shrink-0"
                      variant="inverse-success"
                      @click="addField"
                    >
                      <span class="mdi mdi-18px mdi-playlist-plus"></span>
                      增加
                    </b-button>
                    <b-button
                      v-else-if="currentForm.fields.length > 1"
                      class="p-1 d-inline-flex align-items-center ml-2 flex-shrink-0"
                      variant="inverse-danger"
                      @click="removeField(fieldIndex)"
                    >
                      <span class="mdi mdi-18px mdi-playlist-remove"></span>
                      移除
                    </b-button>
                  </div>
                </VueDraggable>
              </div>

              <div class="d-flex justify-content-center mt-4">
                <b-button
                  class="mr-3"
                  variant="outline-danger"
                  @click="cancel"
                >
                  返回
                </b-button>
                <b-button
                  v-if="selectedReportIndex !== null"
                  class="mr-3"
                  variant="outline-danger"
                  @click="handleDelete"
                >
                  刪除
                </b-button>
                <b-button variant="success" @click="handleSave">
                  儲存
                </b-button>
              </div>
            </b-card>
          </b-overlay>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import bookingApi from "@/apis/booking";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";
import VueDraggable from "vuedraggable";
import deepGet from "lodash/get";

const createEmptyField = (fieldOptions) => fieldOptions[0]?.value ?? null;

const createEmptyForm = (fieldOptions) => ({
  title: "",
  fields: [createEmptyField(fieldOptions)],
});

export default {
  components: { BreadCrumb, VueDraggable },
  data() {
    return {
      showLoading: false,
      bookingPreset: {
        name: "",
        type: "normal",
        config: {
          fields: [],
        },
        report_config: [],
      },
      reports: [],
      selectedReportIndex: null,
      currentForm: {
        title: "",
        fields: [],
      },
    };
  },
  computed: {
    collectionFieldCandidates() {
      const fields = deepGet(this.bookingPreset, "config.fields", []);
      return fields.map((field) => ({
        text: deepGet(field, "config.title") || deepGet(field, "title"),
        value: deepGet(field, "config._id") || deepGet(field, "_id"),
      }));
    },
    fieldOptions() {
      let fields = [
        {
          text: "預約編號",
          value: "booking_number",
        },
      ];

      if (this.bookingPreset.type === "wizard") {
        fields = [
          {
            text: "分店",
            value: "branch_title",
          },
          {
            text: "員工",
            value: "staff_title",
          },
          {
            text: "服務",
            value: "service_title",
          },
          {
            text: "預約編號",
            value: "booking_number",
          },
          ...fields,
        ];
      }

      return [...this.collectionFieldCandidates, ...fields];
    },
  },
  async mounted() {
    await this.fetchBookingPreset();
  },
  methods: {
    normalizeFieldValue(value) {
      const defaultValue = createEmptyField(this.fieldOptions);
      if (value == null || value === "") {
        return defaultValue;
      }

      const isValid = this.fieldOptions.some((option) => option.value === value);
      return isValid ? value : defaultValue;
    },
    normalizeReports(rawReports) {
      if (!Array.isArray(rawReports)) {
        return [];
      }

      return rawReports.map((report) => ({
        title: report?.title || "",
        fields: Array.isArray(report?.fields) && report.fields.length > 0
          ? report.fields.map((field) => this.normalizeFieldValue(field))
          : [createEmptyField(this.fieldOptions)],
      }));
    },
    resetCurrentForm() {
      this.currentForm = createEmptyForm(this.fieldOptions);
    },
    selectReport(index) {
      const report = this.reports[index];
      if (!report) {
        return;
      }

      this.selectedReportIndex = index;
      this.currentForm = {
        title: report.title,
        fields: report.fields.length > 0
          ? report.fields.map((field) => this.normalizeFieldValue(field))
          : [createEmptyField(this.fieldOptions)],
      };
    },
    createNewReport() {
      this.selectedReportIndex = null;
      this.resetCurrentForm();
    },
    addField() {
      this.currentForm.fields.push(createEmptyField(this.fieldOptions));
    },
    removeField(fieldIndex) {
      if (this.currentForm.fields.length <= 1) {
        return;
      }
      this.currentForm.fields.splice(fieldIndex, 1);
    },
    validateForm() {
      const title = this.currentForm.title.trim();
      if (!title) {
        this.$swal("錯誤", "請輸入報表名稱", "error");
        return false;
      }

      const duplicateIndex = this.reports.findIndex(
        (report, index) =>
          report.title.trim() === title && index !== this.selectedReportIndex
      );
      if (duplicateIndex !== -1) {
        this.$swal("錯誤", "報表名稱不可重複", "error");
        return false;
      }

      return true;
    },
    buildReportPayload() {
      return {
        title: this.currentForm.title.trim(),
        fields: [...this.currentForm.fields],
      };
    },
    async fetchBookingPreset() {
      try {
        this.showLoading = true;
        const id = this.$route.params.id;
        const response = await bookingApi.getBookingPreset(id);
        this.bookingPreset = response.data.data;

        if (!this.bookingPreset.config) {
          this.bookingPreset.config = { fields: [] };
        }

        const rawReports = this.bookingPreset.report_config ?? [];

        this.reports = this.normalizeReports(rawReports);

        if (this.reports.length > 0) {
          this.selectReport(0);
        } else {
          this.createNewReport();
        }
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取資料失敗", "error");
      } finally {
        this.showLoading = false;
      }
    },
    async persistReports() {
      const id = this.$route.params.id;
      await bookingApi.updateBookingPresetReportConfig(id, {
        report_config: this.reports,
      });
      this.bookingPreset.report_config = [...this.reports];
    },
    async handleSave() {
      if (!this.validateForm()) {
        return;
      }

      const reportPayload = this.buildReportPayload();

      try {
        this.showLoading = true;

        if (this.selectedReportIndex === null) {
          this.reports.push(reportPayload);
          this.selectedReportIndex = this.reports.length - 1;
        } else {
          this.$set(this.reports, this.selectedReportIndex, reportPayload);
        }

        await this.persistReports();
        this.$swal("成功", "儲存成功", "success");
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "儲存失敗", "error");
      } finally {
        this.showLoading = false;
      }
    },
    async handleDelete() {
      if (this.selectedReportIndex === null) {
        return;
      }

      const result = await this.$swal({
        title: "確定刪除？",
        text: `確定要刪除「${this.currentForm.title}」嗎？`,
        icon: "warning",
        buttons: ["取消", "刪除"],
        dangerMode: true,
      });

      if (!result) {
        return;
      }

      try {
        this.showLoading = true;
        this.reports.splice(this.selectedReportIndex, 1);
        await this.persistReports();

        if (this.reports.length > 0) {
          this.selectReport(Math.min(this.selectedReportIndex, this.reports.length - 1));
        } else {
          this.createNewReport();
        }

        this.$swal("成功", "刪除成功", "success");
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "刪除失敗", "error");
      } finally {
        this.showLoading = false;
      }
    },
    cancel() {
      this.$router.push({ name: "BookingPresetList" });
    },
  },
};
</script>

<style lang="scss" scoped>
.report-fields {
  margin-top: 1.5rem;

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 12px;

    &--last {
      padding: 12px 16px;
      border: 1px dashed #aaa;
      border-radius: 12px;
    }
  }

  &__column-label {
    flex: 0 0 auto;
    min-width: 72px;
    padding: 6px 12px;
    border-radius: 999px;
    background: #f0f2f5;
    color: #6c757d;
    font-size: 0.875rem;
    text-align: center;
  }

  &__select {
    flex: 1 1 auto;
    max-width: 480px;
  }
}

.option-drag-handler {
  cursor: grab;
  color: #adb5bd;
}
</style>
